import React from 'react';
import styled from 'styled-components';
import Dropdown from '../../components/Dropdown';
import LoadingWheel from '../../components/LoadingWheel';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import ButtonRadio from '../TechFormList/components/ButtonRadio';
import buttonTheme from '../../res/styles/theme_button.module.scss';
import { CR_ALERTS } from '../../res/Constants';
import colors from '../../res/styles/Colors';
import { getYearsOfActivityFromCurrent } from '../../service/CommonFunctions';
import { getExtraReportsByYear } from '../../service/ExtraReportService';
import { updateCrAlert } from './../../service/TechFormDetailService';
import { toastInfo, toastSuccess } from './../../ui/ToastHelper';
import { getCurrentYear } from '../../utils/DateFormatter';
import CrAlertList from '../TechFormDetail/tabs/CrAlerts/CrAlertList';
import dimension from './../../res/styles/Responsive';

GenericCrAlertList.propTypes = {};

// Define filter configuration
const cacTypeFilterConfig = {
    label: 'Report type',
    defaultValueIndex: 0,
    values: [
        { name: 'All', value: null },
        { name: 'Crop', value: 1 },
        { name: 'Nursery', value: 2 }
    ]
};

function GenericCrAlertList() {
    const [loading, setLoading] = React.useState(true);
    const [extraReportList, setExtraReportList] = React.useState([]);
    const [selectedFilterYear, setSelectedFilterYear] = React.useState(getCurrentYear());
    const [selectedCacType, setSelectedCacType] = React.useState(cacTypeFilterConfig.values[0].value);
    const [isPending, startTransition] = React.useTransition();

    React.useEffect(() => {
        setLoading(true);
        setExtraReportList([]);
        getExtraReportsByYear(selectedFilterYear, (fetchedList) => {
            if (fetchedList != null) {
                // Use startTransition to keep loading wheel visible while React renders the large list
                startTransition(() => {
                    setExtraReportList(fetchedList);
                    setLoading(false);
                });
            } else {
                setLoading(false);
            }
        });
    }, [selectedFilterYear]);

    const onItemUpdated = (crAlertData) => {
        toastInfo('Updating CR-Alert...');

        updateCrAlert(crAlertData, (updatedCrAlert) => {
            if (updatedCrAlert != null) {
                toastSuccess('CR-Alert successfully updated.');
                setExtraReportList(
                    [...extraReportList].map((obj) =>
                        obj.pk === crAlertData.pk ? crAlertData : obj
                    )
                );
            }
        });
    };

    const onCrAlertDeleted = (crAlertPk) => {
        setExtraReportList(extraReportList.filter((obj) => obj.pk !== crAlertPk));
    };

    // Filter the list based on selected CAC type - memoized for performance
    const filteredExtraReportList = React.useMemo(() => {
        let filtered = extraReportList;

        // Filter by orderType if a specific type is selected
        if (selectedCacType !== null) {
            filtered = filtered.filter((report) => report.orderType === selectedCacType);
        }

        // Remove duplicates based on pk (primary key)
        const uniqueMap = new Map();
        filtered.forEach((report) => {
            if (!uniqueMap.has(report.pk)) {
                uniqueMap.set(report.pk, report);
            }
        });

        return Array.from(uniqueMap.values());
    }, [extraReportList, selectedCacType]);

    // Get default index for cac type filter
    const defaultCacTypeIndex = cacTypeFilterConfig.defaultValueIndex;

    const yearFilter = (
        <div key="year-filter">
            <YearFilter>Years</YearFilter>
            <StyledDropdown
                options={getYearsOfActivityFromCurrent().map(String)}
                emptyOption={false}
                defaultValue={selectedFilterYear}
                onChange={(year) => setSelectedFilterYear(parseInt(year))}
            />
        </div>
    );

    const cacTypeFilter = (
        <div key="cac-type-filter">
            <YearFilter>{cacTypeFilterConfig.label}</YearFilter>
            <ButtonRadio
                className={buttonTheme.lighterGreen}
                onActiveStyle={buttonTheme.green}
                defaultValueIndex={defaultCacTypeIndex}
                values={cacTypeFilterConfig.values}
                onChangeValue={(selectedName) => {
                    // Prevent deselection when clicking on the already selected button
                    if (selectedName === null) {
                        return;
                    }

                    const selectedFilter = cacTypeFilterConfig.values.find(
                        (item) => item.name === selectedName
                    );

                    // Use startTransition to show loading while filtering large lists
                    startTransition(() => {
                        setSelectedCacType(selectedFilter?.value);
                    });
                }}
            />
        </div>
    );

    return (
        <div>
            <NavigationBar />
            <StyledContainer>
                <h2>{CR_ALERTS}</h2>
                <CrAlertList
                    isGeneric
                    list={loading || isPending ? [] : filteredExtraReportList}
                    onCrAlertUpdated={onItemUpdated}
                    onCrAlertDeleted={onCrAlertDeleted}
                    filters={[yearFilter, cacTypeFilter]}
                />
                {(loading || isPending) && (
                    <LoadingContainer>
                        <LoadingWheel />
                    </LoadingContainer>
                )}
            </StyledContainer>
        </div>
    );
}
export default GenericCrAlertList;

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 90vh;
    padding: 1rem 5% 0;
    margin: 0 auto;
    background-color: ${colors.light_gray};
`;

const StyledDropdown = styled(Dropdown)`
    min-width: 70px;

    @media ${dimension.md} {
        min-width: 150px;
    }
`;

const YearFilter = styled.p`
    margin-bottom: 0.1rem;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem 0;
    min-height: 200px;
`;
